import { useMemo } from "react";
import { useRouter } from "next/router";

import { generateUrlArguments, getCurrentDate, timezone } from "@/utils";

import useForm from "@/hooks/useHookForm";
import useFormPayment from "@/hooks/useFormPayment";
import usePromoCode from "@/hooks/usePromoCodeInput";

import { useAppSelector } from "@/hooks/useRedux";

const formDefaultValue = {
  name: "",
  email: "",
  mobile: "",
  mobileCountryCode: "+91"
}
interface useBookConsultationFormProps {
  packData?: any;
  setFormError?: (error: string) => void;
  setFormData?: any;
}

const useBookConsultationForm = ({
  packData,
  setFormError = () => { },
  setFormData= () => {}
}: useBookConsultationFormProps) => {
  const router = useRouter();

  const { query } = router || {};

  let slug =
    typeof query?.slug === "string" ? query?.slug : query?.slug?.[0] || "";

  const { countryCodeData } = useAppSelector((state) => state.dashboard);
  const { data } = useAppSelector((state) => state.bootCamp);
  const { isSpecialPackPurchase = false } = data?.heroSection
  const isGeneralYogaPage = data?.pageType === "GENERAL_YOGA";
  const isGeneralNutritionPage = data?.pageType === "GENERAL_NUTRITION";
  const isILAPage = slug === "indians-living-abroad";
  const isTransformationProgramPage = slug === "90-days-transformation"
  const isTtcPage = data?.conditionKey === "yoga-trainer-foundation-program"

  const { title, nutritionGoalID, isTrialPack } = packData;

  let countryCodeDefault = useMemo(
    () => countryCodeData?.default?.code || "+91",
    [countryCodeData]
  );

  let _timezone;
  if (isILAPage) {
    _timezone = timezone === "Asia/Dubai" ? timezone : "US/Central";
    countryCodeDefault =
      countryCodeDefault === "+91" ? "+1" : countryCodeDefault;
  }

  const defaultValues = useMemo(
    () => ({
      name: "",
      email: "",
      mobileCountryCode: countryCodeDefault,
      mobile: "",
      ...(isGeneralYogaPage || isGeneralNutritionPage
        ? { nutritionGoalID: "" }
        : {}),
      ...(nutritionGoalID ? { nutritionGoalID } : {}),
      ...(isTtcPage ? { totalHours: "" } : {}),
    }),
    [
      countryCodeDefault,
      isGeneralYogaPage,
      isGeneralNutritionPage,
      nutritionGoalID,
    ]
  );

  const urlsArguments = generateUrlArguments();

  const { methods, resetData } = useForm({
    defaultValues,
  });

  const {
    isPromoCodeLoading,
    isPromoCodeApplied,
    promoCodeData,
    promoCodeError,
    handlePromoCodeChange,
    applyPromoCode,
    removeAppliedPromoCode,
    promoCodeButtonText,
    preApplyPromoCode
  } = usePromoCode({
    methods,
    packData,
    additionalData: {
      ...(isILAPage ? { timezone: _timezone } : {}),
      original_price: packData?.price,
      ...(isGeneralNutritionPage || nutritionGoalID || isTrialPack
        ? {}
        : {
          bundle_slug: packData?.purchasedPlanSlug,
          package_choice: "REGULAR",
          package_slug: null,
        })
    },
  });

  const getSuccessPagePath = () => {
    if (data?.pageType === "YOGA" || data?.pageType === "GENERAL_YOGA") {
      return "/plans/payments/yoga/success/";
    } else {
      return "/plans/payments/nutrition/success/";
    }
  };

  const onSuccessCallback = (paymentData) => {
    resetData();
    setFormData(formDefaultValue)
    router.push({
      pathname: getSuccessPagePath(),
      query: {
        ...router?.query,
        boot_camp_name: title,
        // boot_camp_start_Date: startDate,
        // boot_camp_picture: thumbnail,
        currency: paymentData?.currency,
        amount_paid: paymentData?.amount_paid,
        boot_camp_type: data?.pageType,
        isTrialPack: isTrialPack,
        ...(isTtcPage ? { startDate: packData?.startDate, conditionKey: data?.conditionKey } : {}),
      },
    });
  };

  const onFailureCallback = () => {
    resetData();
    setFormData(formDefaultValue)
    router.push({
      pathname: "/payments/failure/",
      query: router?.query,
    });
  };

  const { isLoading, onBlur, onPackPurchase } = useFormPayment({
    methods,
    packData,
    urlsArguments,
    timezone,
    onSuccessCallback,
    onFailureCallback,
    isPromoCodeApplied,
    setFormError
  });

  const { handleSubmit, getValues } = methods;

  const packPurchaseHandler = handleSubmit((formValues) => {
    const { mobileCountryCode } = getValues();
    if (isILAPage && packData?.price === 0 && mobileCountryCode === "+91") {
      setFormError("This pack is not available in your country");
    } else {
      setFormError("");
      const todaysDate = getCurrentDate()
      let additionalData = {
        ...(isSpecialPackPurchase ? { special_pack_purchase: isSpecialPackPurchase } : { special_pack_purchase: true })
      } as any;
      if (isTrialPack) {
        additionalData = { is_trial_sales: true };
      } else if (isGeneralNutritionPage || nutritionGoalID) {
        additionalData = {};
      }
      if (isTransformationProgramPage) {
        additionalData = {
          ...additionalData,
          nutritionist_id: 48,
          package_start_date: todaysDate,
          coach_id: 18,
          page_type: "health_package_renewal"
        }
      }
      let keys = []

      if (isTransformationProgramPage) {
        keys = ["nutritionist_id", "package_start_date", "coach_id", "page_type"]
      }

      return onPackPurchase({
        keys,
        ...formValues,
        ...additionalData,
      });
    }
  });

  return {
    methods,
    isLoading,
    onBlur,
    packPurchaseHandler,
    isPromoCodeLoading,
    isPromoCodeApplied,
    promoCodeData,
    promoCodeError,
    handlePromoCodeChange,
    applyPromoCode,
    removeAppliedPromoCode,
    promoCodeButtonText,
    preApplyPromoCode
  };
};

export default useBookConsultationForm;
